// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	AnchorIcon,
	CoffeeIcon,
	CrownIcon,
	DiamondIcon,
	DropIcon,
	FireIcon,
	FlowerIcon,
	HeartIcon,
	type Icon,
	type IconProps,
	LeafIcon,
	LightningIcon,
	MoonStarsIcon,
	MusicNotesIcon,
	PawPrintIcon,
	RocketLaunchIcon,
	ShieldStarIcon,
	SkullIcon,
	SparkleIcon,
	StarIcon,
	SwordIcon,
} from '@phosphor-icons/react';
import type React from 'react';
import {forwardRef, useId} from 'react';

export type ServerTagBadgeId =
	| 'leaf'
	| 'sword'
	| 'heart'
	| 'flame'
	| 'droplet'
	| 'skull'
	| 'moon'
	| 'bolt'
	| 'gem'
	| 'mushroom'
	| 'star'
	| 'crown'
	| 'rose'
	| 'paw'
	| 'shield'
	| 'rocket'
	| 'music'
	| 'sparkle'
	| 'anchor'
	| 'coffee';

export const SERVER_TAG_FEATURED_BADGE_IDS: ReadonlyArray<ServerTagBadgeId> = [
	'leaf',
	'sword',
	'heart',
	'flame',
	'droplet',
	'skull',
	'moon',
	'bolt',
	'gem',
	'mushroom',
];

export const SERVER_TAG_ALL_BADGE_IDS: ReadonlyArray<ServerTagBadgeId> = [
	...SERVER_TAG_FEATURED_BADGE_IDS,
	'star',
	'crown',
	'rose',
	'paw',
	'shield',
	'rocket',
	'music',
	'sparkle',
	'anchor',
	'coffee',
];

export const SERVER_TAG_DEFAULT_BADGE_ID: ServerTagBadgeId = 'sword';

export const SERVER_TAG_COLOR_PRESETS: ReadonlyArray<number> = [
	0xf47fff, 0xff73fa, 0xed4245, 0xfaa61a, 0xfee75c, 0x57f287, 0x1abc9c, 0x3ba55d, 0x5865f2, 0x00b0f4, 0x9b59b6,
	0xe91e63, 0xb9bbbe, 0x747f8d, 0xffffff,
];

export const SERVER_TAG_DEFAULT_COLOR = 0xf47fff;

interface ServerTagPalette {
	top: string;
	mid: string;
	bottom: string;
}

function clampByte(value: number): number {
	return Math.max(0, Math.min(255, Math.round(value)));
}

function parseCssHexColor(color: string): {r: number; g: number; b: number} | null {
	const match = /^#?([0-9a-f]{6})$/i.exec(color.trim());
	if (!match) return null;
	const value = Number.parseInt(match[1]!, 16);
	return {r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255};
}

function mixRgb(
	from: {r: number; g: number; b: number},
	to: {r: number; g: number; b: number},
	amount: number,
): string {
	const t = Math.max(0, Math.min(1, amount));
	const r = clampByte(from.r + (to.r - from.r) * t);
	const g = clampByte(from.g + (to.g - from.g) * t);
	const b = clampByte(from.b + (to.b - from.b) * t);
	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Glossy three-stop ramp from one accent swatch: lit top, true colour, slightly deeper base. */
export function resolveServerTagPalette(accent: string): ServerTagPalette {
	const rgb = parseCssHexColor(accent) ?? {r: 244, g: 127, b: 255};
	return {
		top: mixRgb(rgb, {r: 255, g: 255, b: 255}, 0.45),
		mid: mixRgb(rgb, {r: 255, g: 255, b: 255}, 0.08),
		bottom: mixRgb(rgb, {r: 14, g: 14, b: 18}, 0.32),
	};
}

/** Phosphor has no mushroom; drawn on the same 256 grid in the "fill" style so it matches the set. */
const MushroomGlyph: Icon = forwardRef<SVGSVGElement, IconProps>(({size = 20, color = 'currentColor', weight: _weight, mirrored: _mirrored, alt: _alt, ...rest}, ref) => (
	<svg ref={ref} width={size} height={size} viewBox="0 0 256 256" fill={color} xmlns="http://www.w3.org/2000/svg" {...rest}>
		<path d="M128 24C66.6 24 20 71.3 20 124a20 20 0 0 0 20 20h176a20 20 0 0 0 20-20c0-52.7-46.6-100-108-100Zm-36 72a16 16 0 1 1 16-16 16 16 0 0 1-16 16Zm76 4a12 12 0 1 1 12-12 12 12 0 0 1-12 12Z" />
		<path d="M92 160h72l7.4 54.9A20 20 0 0 1 151.6 238h-47.2a20 20 0 0 1-19.8-23.1Z" />
	</svg>
)) as Icon;
MushroomGlyph.displayName = 'MushroomGlyph';

const BADGE_ICON_MAP: Record<ServerTagBadgeId, Icon> = {
	leaf: LeafIcon,
	sword: SwordIcon,
	heart: HeartIcon,
	flame: FireIcon,
	droplet: DropIcon,
	skull: SkullIcon,
	moon: MoonStarsIcon,
	bolt: LightningIcon,
	gem: DiamondIcon,
	mushroom: MushroomGlyph,
	star: StarIcon,
	crown: CrownIcon,
	rose: FlowerIcon,
	paw: PawPrintIcon,
	shield: ShieldStarIcon,
	rocket: RocketLaunchIcon,
	music: MusicNotesIcon,
	sparkle: SparkleIcon,
	anchor: AnchorIcon,
	coffee: CoffeeIcon,
};

/**
 * Server tag emblem: a solid Phosphor glyph filled with a glossy accent gradient and a hairline dark rim
 * (drop-shadow) so it stays crisp at 12-14 px on any background - the Discord guild-tag look.
 */
export function ServerTagBadgeGlyph({
	badgeId,
	className,
	size = 20,
	accent = '#f47fff',
}: {
	badgeId: ServerTagBadgeId;
	className?: string;
	size?: number;
	accent?: string;
}): React.ReactElement {
	const gradientId = `stg-${useId().replace(/:/g, '')}`;
	const palette = resolveServerTagPalette(accent);
	const Glyph = BADGE_ICON_MAP[badgeId] ?? SwordIcon;
	return (
		<>
			<svg width="0" height="0" aria-hidden style={{position: 'absolute', overflow: 'hidden'}}>
				<defs>
					<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
						<stop offset="0" stopColor={palette.top} />
						<stop offset="0.5" stopColor={palette.mid} />
						<stop offset="1" stopColor={palette.bottom} />
					</linearGradient>
				</defs>
			</svg>
			<Glyph
				weight="fill"
				size={size}
				color={`url(#${gradientId})`}
				className={className}
				aria-hidden
				style={{flexShrink: 0, filter: 'drop-shadow(0 0.5px 0 rgba(0, 0, 0, 0.55)) drop-shadow(0 0 0.5px rgba(0, 0, 0, 0.35))'}}
			/>
		</>
	);
}

export function colorNumberToCss(color: number): string {
	return `#${(color >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
}
